import { ecId, ecLean, handBack, record, upper, type Row } from '../ability/context';
import { fromBaseUuid } from '../compile/from-base-context';
import { readItemField } from '../compile/item-field-reader';
import { inventoryRowList, readInventoryField, readInventoryRows } from '../compile/inventory-raw-readers';
import { emptySpawnPlan, type SpawnRowPlan } from '../spawn/row-plan';
import { statusRows, STATUS_IDS } from '../statuses/expiry';
import { statusScalar } from '../actions/activity';
import { merchantDeathPictures } from './merchant';

/** Physical holders are also status targets. Keep the same object identities as the action's
 * member map so bulk retirements clear every projection before a later loss hook runs. */
export function deathHolders(roots: readonly Row[]): Row[] {
    const seen = new Set<Row>(), result: Row[] = [];
    const visit = (row: Row): void => {
        if (seen.has(row)) return;
        seen.add(row); result.push(row);
        for (const child of [...readInventoryRows(row, 'inventory'), ...readInventoryRows(row, 'inventory_in_container'),
            ...readInventoryRows(row, 'encounter_inventories_arr')]) visit(child);
    };
    roots.forEach(visit); return result;
}

/** destroy_creature's raw storage phase. Source-linked effects and orphan concentration are
 * executed by the shared death status planner, rather than silently bulk-deleted here. */
export function deathRawCreatureBase(id: string, holders: readonly Row[]): Row | null {
    const bases = holders.flatMap(holder => {
        const base = record(record(holder._entity_base)?.base);
        return base ? [base, ...inventoryRowList(base.encounter_creatures_arr)] : [];
    });
    const raw = bases.find(base => ecId(base) === id.toUpperCase()) ?? null;
    if (!raw) return null;
    // Attachment lists can be omitted when empty or encoded as keyed PHP objects.
    // Keep the held revision immutable and retain its identity when already normalized.
    let normalized: Row | undefined;
    for (const key of ['encounter_creatures_spells_prepared', 'pictures']) {
        const rows = raw[key];
        if (rows == null || record(rows)) (normalized ??= { ...raw })[key] = inventoryRowList(rows);
    }
    return normalized ?? raw;
}

export function planDepartureRows(target: Row, holders: readonly Row[], options: { storageCleanup?: boolean; departedAlive?: boolean } = {}): SpawnRowPlan {
    const id = ecId(target), raw = deathRawCreatureBase(id, holders);
    // A raw creature holder is required even when its attachment lists are empty; storage cleanup never reads it.
    if (!options.storageCleanup && (!raw || !Array.isArray(raw.encounter_creatures_spells_prepared) || !Array.isArray(raw.pictures)))
        handBack('death:departure-attachments-not-held');
    const plan = emptySpawnPlan();
    if (!options.storageCleanup) plan.writes.push({ table: 'encounter_creatures', op: 'update', row: { encounter_creature_id: id,
        encounter_creature_deleted: 1, encounter_creature_dead: 1, v_current: 0, v_temp: 0 },
        before: { encounter_creature_deleted: Number(ecLean(target, 'encounter_creature_deleted') ?? 0) } });
    for (const prepared of options.storageCleanup ? [] : inventoryRowList(raw!.encounter_creatures_spells_prepared)) {
        const key = readItemField(prepared, 'encounter_creature_spell_prepared_i');
        if (!Number.isSafeInteger(key) || Number(key) <= 0) handBack('death:prepared-spell-identity-not-held');
        plan.writes.push({ table: 'encounter_creatures_spells_prepared', op: 'update',
            row: { encounter_creature_spell_prepared_i: key, encounter_creature_spell_prepared_deleted: 1 },
            before: { encounter_creature_spell_prepared_deleted: Number(readItemField(prepared, 'encounter_creature_spell_prepared_deleted') ?? 0) } });
    }
    const pictures = (rows: readonly Row[], owner: string, inventory = false): void => {
        for (const picture of rows) {
            if (upper(readItemField(picture, 'picture_attached_id')) !== owner) continue;
            const pictureId = readItemField(picture, 'picture_id');
            if (!fromBaseUuid(pictureId)) handBack('death:departure-picture-identity-not-held');
            plan.writes.push({ table: inventory ? 'death_inventory_pictures' : 'pictures', op: 'update',
                row: { picture_id: pictureId, picture_deleted: 1 },
                before: { picture_deleted: Number(readItemField(picture, 'picture_deleted') ?? 0) } });
        }
    };
    if (!options.storageCleanup) pictures(inventoryRowList(raw!.pictures), id);
    const owned = new Set<string>([id]);
    // Possession, rather than the presentation tree's shape, decides what is destroyed.
    let changed = true;
    while (changed) {
        changed = false;
        for (const row of holders) {
            const child = upper(readInventoryField(row, 'inventory_id'));
            if (child && !owned.has(child) && owned.has(upper(readInventoryField(row, 'possess_id')))) {
                owned.add(child); changed = true;
            }
        }
    }
    for (const row of holders) {
        const inventoryId = upper(readInventoryField(row, 'inventory_id'));
        if (!inventoryId) continue;
        const body = upper(readInventoryField(row, 'inventory_encounter_creature_id')) === id
            && Number(readInventoryField(row, 'inventory_deleted') ?? 0) === 0;
        if (options.storageCleanup && (options.departedAlive || !body)) continue;
        if (!owned.has(inventoryId) && !body) continue;
        plan.writes.push({ table: 'inventory', op: 'update',
            row: { inventory_id: inventoryId, inventory_deleted: 1, ...(body ? { item_qty: 0 } : {}) },
            before: { possess_id: String(readInventoryField(row, 'possess_id') ?? ''),
                inventory_deleted: Number(readInventoryField(row, 'inventory_deleted') ?? 0),
                ...(body ? { item_qty: Number(readInventoryField(row, 'item_qty') ?? 1) } : {}) } });
        // Consuming the linked body does not recursively delete its contents or status rows.
        if (options.storageCleanup || !owned.has(inventoryId)) continue;
        pictures(merchantDeathPictures(row, {}), inventoryId, true);
        for (const status of statusRows(row, 'target')) {
            const item = upper(statusScalar(status, 'status_item_id'));
            if ([STATUS_IDS.riding, STATUS_IDS.ridden, STATUS_IDS.piloting, STATUS_IDS.piloted].some(id => id === item))
                handBack('death:departure-inventory-transport-not-modeled');
            const statusId = statusScalar(status, 'status_id');
            if (!fromBaseUuid(statusId)) handBack('death:departure-status-identity-not-held');
            plan.writes.push({ table: 'statuses', op: 'update', row: { status_id: statusId, status_deleted: 1 },
                before: { status_deleted: Number(statusScalar(status, 'status_deleted') ?? 0), status_target_id: inventoryId } });
        }
    }
    return plan;
}
